import { useCallback, useEffect, useRef, useState } from "react";

import { GridSelection } from "@glideapps/glide-data-grid";

interface UseGridKeyboardParams {
  selection?: GridSelection;
  setSelection?: (selection: GridSelection | undefined) => void;
  onEscapeWhenIdle?: () => void;
}

/**
 * Hook for managing grid keyboard interactions (search, escape handling)
 */
export function useGridKeyboard(params?: UseGridKeyboardParams) {
  const { selection, setSelection, onEscapeWhenIdle } = params ?? {};
  const [isSearchVisible, setIsSearchVisible] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const isSearchVisibleRef = useRef(false);
  const isEditingRef = useRef(false);
  const selectionRef = useRef<GridSelection | undefined>(undefined);
  const onEscapeWhenIdleRef = useRef(onEscapeWhenIdle);

  useEffect(() => {
    isSearchVisibleRef.current = isSearchVisible;
  }, [isSearchVisible]);

  useEffect(() => {
    isEditingRef.current = isEditing;
  }, [isEditing]);

  useEffect(() => {
    selectionRef.current = selection;
  }, [selection]);

  useEffect(() => {
    onEscapeWhenIdleRef.current = onEscapeWhenIdle;
  }, [onEscapeWhenIdle]);

  const onSearchClose = useCallback(() => {
    setIsSearchVisible(false);
  }, []);

  // Check if grid has an active cell selection
  const hasActiveSelection = useCallback(() => {
    const sel = selectionRef.current;
    return sel?.current !== undefined || (sel?.rows?.length ?? 0) > 0;
  }, []);

  // Document-level ESC handler with capture phase
  useEffect(() => {
    const handleDocumentKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;

      // If search is visible, close it
      if (isSearchVisibleRef.current) {
        e.stopPropagation();
        e.preventDefault();
        setIsSearchVisible(false);
        return;
      }

      // If editing a cell, let the grid handle it but mark editing as done
      if (isEditingRef.current) {
        // Don't stop - let grid handle cell exit
        // Just update our state
        setIsEditing(false);
        return;
      }

      // If there's a selection, clear it
      if (hasActiveSelection() && setSelection) {
        e.stopPropagation();
        e.preventDefault();
        setSelection(undefined);
        return;
      }

      // Nothing active - call the idle callback (to close modal)
      if (onEscapeWhenIdleRef.current) {
        e.stopPropagation();
        e.preventDefault();
        onEscapeWhenIdleRef.current();
      }
    };

    // Use capture phase to intercept before DataEditor
    document.addEventListener("keydown", handleDocumentKeyDown, true);
    return () => document.removeEventListener("keydown", handleDocumentKeyDown, true);
  }, [hasActiveSelection, setSelection]);

  const handleGridKeyDown = useCallback((e: React.KeyboardEvent) => {
    // This is now mostly a no-op since we handle at document level
    // Keep it for potential future non-ESC key handling
    if (e.key !== "Escape") {
      return;
    }
  }, []);

  // Global keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "f") {
        e.preventDefault();
        setIsSearchVisible(true);
        return;
      }
    };

    document.addEventListener("keydown", handleKeyDown, false);
    return () => document.removeEventListener("keydown", handleKeyDown, false);
  }, []);

  return {
    isSearchVisible,
    setIsSearchVisible,
    isEditing,
    setIsEditing,
    onSearchClose,
    handleGridKeyDown
  };
}
